import React, { useState } from 'react';
import {
    Mail,
    Phone,
    MapPin,
    Clock,
    Send,
    MessageSquare,
    Building2,
    User,
    AtSign,
    FileText,
    CheckCircle2
} from 'lucide-react';
import SimpleReactValidator from 'simple-react-validator';
import PageLayout from '../../components/Layout/PageLayout';
import PageHeader from '../../components/PageHeader/PageHeader';
import './ModernContactPage.scss';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const ModernContactPage = () => {
    const [forms, setForms] = useState({
        name: '',
        email: '',
        phone: '',
        company: '',
        subject: '',
        message: ''
    });

    const [validator] = useState(new SimpleReactValidator({
        className: 'jk-contact-form__error'
    }));

    const [submitted, setSubmitted] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');

    const changeHandler = e => {
        setForms({ ...forms, [e.target.name]: e.target.value });
        if (validator.allValid()) {
            validator.hideMessages();
        } else {
            validator.showMessages();
        }
    };

    const submitHandler = async e => {
        e.preventDefault();
        if (!validator.allValid()) {
            validator.showMessages();
            return;
        }
        validator.hideMessages();
        setErrorMsg('');
        setSubmitting(true);
        try {
            const response = await fetch(`${API_BASE_URL}/contact/submit`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(forms)
            });
            const result = await response.json().catch(() => ({}));
            if (!response.ok || !result.success) {
                throw new Error(result.error || 'Failed to send your message. Please try again.');
            }
            setSubmitted(true);
            setForms({ name: '', email: '', phone: '', company: '', subject: '', message: '' });
            setTimeout(() => setSubmitted(false), 6000);
        } catch (err) {
            setErrorMsg(err.message || 'Something went wrong. Please try again or email us directly.');
        } finally {
            setSubmitting(false);
        }
    };

    const contactInfo = [
        {
            icon: <Phone size={24} />,
            title: 'Phone',
            details: ['+1 (240) 226-6333'],
            color: 'primary'
        },
        {
            icon: <Mail size={24} />,
            title: 'Email',
            details: ['info@jaaktech.net'],
            color: 'blue'
        },
        {
            icon: <MapPin size={24} />,
            title: 'Address',
            details: ['3342 Bladensburg Rd Suite B06', 'Brentwood, Maryland, 20722'],
            color: 'green'
        },
        {
            icon: <Clock size={24} />,
            title: 'Business Hours',
            details: ['Mon - Fri: 8:00 AM - 6:00 PM'],
            color: 'orange'
        }
    ];

    const inquiryTypes = [
        'General Inquiry',
        'Government Procurement',
        'Hardware & Software',
        'IT Services',
        'Partnership Opportunities',
        'Technical Support'
    ];

    return (
        <PageLayout>
            <PageHeader
                title="Contact Us"
                subtitle="Get in touch with our team for all your IT procurement and service needs"
                breadcrumbs={[{ label: 'Contact' }]}
            />

            {/* Contact Info Cards */}
            <section className="jk-contact-info">
                <div className="jk-contact-info__container">
                    <div className="jk-contact-info__grid">
                        {contactInfo.map((info, index) => (
                            <div key={index} className={`jk-contact-info__card jk-contact-info__card--${info.color}`}>
                                <div className="jk-contact-info__icon">
                                    {info.icon}
                                </div>
                                <h3 className="jk-contact-info__title">{info.title}</h3>
                                {info.details.map((detail, idx) => (
                                    <p key={idx} className="jk-contact-info__text">{detail}</p>
                                ))}
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Contact Form Section */}
            <section className="jk-contact-section">
                <div className="jk-contact-section__container">
                    <div className="jk-contact-section__grid">
                        {/* Form */}
                        <div className="jk-contact-form">
                            <div className="jk-contact-form__header">
                                <span className="jk-section-label">
                                    <MessageSquare size={16} />
                                    Get In Touch
                                </span>
                                <h2 className="jk-contact-form__title">
                                    Send Us a <span className="highlight">Message</span>
                                </h2>
                                <p className="jk-contact-form__subtitle">
                                    Have a question or need assistance? Fill out the form below and our team will get back to you promptly.
                                </p>
                            </div>

                            {submitted && (
                                <div className="jk-contact-form__success">
                                    <CheckCircle2 size={24} />
                                    <span>Thank you! Your message has been sent successfully. We'll be in touch soon.</span>
                                </div>
                            )}

                            {errorMsg && (
                                <div
                                    className="jk-contact-form__error-banner"
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.6rem',
                                        background: '#FEF2F2',
                                        color: '#B91C1C',
                                        border: '1px solid #FECACA',
                                        borderRadius: '12px',
                                        padding: '0.85rem 1rem',
                                        marginBottom: '1rem',
                                        fontSize: '0.9375rem'
                                    }}
                                >
                                    <span>{errorMsg}</span>
                                </div>
                            )}

                            <form onSubmit={submitHandler} className="jk-contact-form__form">
                                <div className="jk-contact-form__row">
                                    <div className="jk-contact-form__field">
                                        <label className="jk-contact-form__label">
                                            <User size={16} />
                                            Full Name *
                                        </label>
                                        <input
                                            type="text"
                                            name="name"
                                            value={forms.name}
                                            onChange={changeHandler}
                                            onBlur={changeHandler}
                                            className="jk-contact-form__input"
                                            placeholder="John Doe"
                                        />
                                        {validator.message('name', forms.name, 'required|alpha_space')}
                                    </div>
                                    <div className="jk-contact-form__field">
                                        <label className="jk-contact-form__label">
                                            <AtSign size={16} />
                                            Email Address *
                                        </label>
                                        <input
                                            type="email"
                                            name="email"
                                            value={forms.email}
                                            onChange={changeHandler}
                                            onBlur={changeHandler}
                                            className="jk-contact-form__input"
                                            placeholder="john@example.com"
                                        />
                                        {validator.message('email', forms.email, 'required|email')}
                                    </div>
                                </div>

                                <div className="jk-contact-form__row">
                                    <div className="jk-contact-form__field">
                                        <label className="jk-contact-form__label">
                                            <Phone size={16} />
                                            Phone Number *
                                        </label>
                                        <input
                                            type="tel"
                                            name="phone"
                                            value={forms.phone}
                                            onChange={changeHandler}
                                            onBlur={changeHandler}
                                            className="jk-contact-form__input"
                                            placeholder="+1 (555) 000-0000"
                                        />
                                        {validator.message('phone', forms.phone, 'required|phone')}
                                    </div>
                                    <div className="jk-contact-form__field">
                                        <label className="jk-contact-form__label">
                                            <Building2 size={16} />
                                            Company / Organization
                                        </label>
                                        <input
                                            type="text"
                                            name="company"
                                            value={forms.company}
                                            onChange={changeHandler}
                                            className="jk-contact-form__input"
                                            placeholder="Your organization"
                                        />
                                    </div>
                                </div>

                                <div className="jk-contact-form__field">
                                    <label className="jk-contact-form__label">
                                        <FileText size={16} />
                                        Inquiry Type
                                    </label>
                                    <select
                                        name="subject"
                                        value={forms.subject}
                                        onChange={changeHandler}
                                        className="jk-contact-form__select"
                                    >
                                        <option value="">Select an inquiry type</option>
                                        {inquiryTypes.map((type, index) => (
                                            <option key={index} value={type}>{type}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="jk-contact-form__field">
                                    <label className="jk-contact-form__label">
                                        <MessageSquare size={16} />
                                        Message *
                                    </label>
                                    <textarea
                                        name="message"
                                        value={forms.message}
                                        onChange={changeHandler}
                                        onBlur={changeHandler}
                                        className="jk-contact-form__textarea"
                                        placeholder="Tell us how we can help you..."
                                        rows={5}
                                    />
                                    {validator.message('message', forms.message, 'required')}
                                </div>

                                <button type="submit" className="jk-btn jk-btn--primary jk-btn--full" disabled={submitting}>
                                    <Send size={20} />
                                    {submitting ? 'Sending...' : 'Send Message'}
                                </button>
                            </form>
                        </div>

                        {/* Info Sidebar */}
                        <div className="jk-contact-sidebar">
                            <div className="jk-contact-sidebar__card">
                                <h3 className="jk-contact-sidebar__title">Why Contact Us?</h3>
                                <ul className="jk-contact-sidebar__list">
                                    <li>
                                        <CheckCircle2 size={18} />
                                        <span>Expert guidance on IT procurement</span>
                                    </li>
                                    <li>
                                        <CheckCircle2 size={18} />
                                        <span>Government-compliant solutions</span>
                                    </li>
                                    <li>
                                        <CheckCircle2 size={18} />
                                        <span>Competitive pricing on hardware & software</span>
                                    </li>
                                    <li>
                                        <CheckCircle2 size={18} />
                                        <span>Fast response times</span>
                                    </li>
                                    <li>
                                        <CheckCircle2 size={18} />
                                        <span>Dedicated account management</span>
                                    </li>
                                </ul>
                            </div>

                            <div className="jk-contact-sidebar__card jk-contact-sidebar__card--dark">
                                <h3 className="jk-contact-sidebar__title">Quick Response</h3>
                                <p className="jk-contact-sidebar__text">
                                    Our team typically responds within 24 hours during business days. For urgent inquiries, please call us directly.
                                </p>
                                <a href="tel:+15551234567" className="jk-btn jk-btn--outline-light">
                                    <Phone size={18} />
                                    Call Now
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Map Section */}
            <section className="jk-contact-map">
                <div className="jk-contact-map__container">
                    <div className="jk-contact-map__header">
                        <span className="jk-section-label">
                            <MapPin size={16} />
                            Our Location
                        </span>
                        <h2 className="jk-contact-map__title">
                            Visit Our <span className="highlight">Office</span>
                        </h2>
                    </div>
                    <div className="jk-contact-map__wrapper">
                        <iframe
                            title="Jaak Tech Location"
                            src="https://maps.google.com/maps?q=3342%20Bladensburg%20Rd%20Suite%20B06,%20Brentwood,%20Maryland%2020722&t=&z=16&ie=UTF8&iwloc=&output=embed"
                            allowFullScreen=""
                            loading="lazy"
                            referrerPolicy="no-referrer-when-downgrade"
                        />
                    </div>
                </div>
            </section>
        </PageLayout>
    );
};

export default ModernContactPage;
